import { UntypedFormGroup } from '@angular/forms';
import omit from 'lodash-es/omit';
import isEmpty from 'lodash-es/isEmpty';

import { BookingArrangementVM, Travelers, HotelBookingPayload } from '@booking/entities/booking-arrangement';
import { BillingPayload, BillingResponse } from '@billing/entities/billing.payload';
import { createAt, payloadToCreate } from '@billing/mappers/billing-id.mapper';
import { TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { TravelerTypes } from '@pax/entities/traveler-info';
import { paxByGuestRoom, Room } from '@shared/base-search-bar/entities/room.entities';
import { formatTravelerDate } from '@booking/mappers/traveler-date.mapper';
import { TravelerItemUI } from '@pax/entities/pax-ui';
import { environment as ENV } from '@environment';
import { UserGender } from '@booking/entities/user-gender';
import { AssistanceBookPayload } from '@shared/travel-assistance/entities/booking-assistance';
import { assistanceBookPayload } from '@booking/mappers/assistance-search-payload.mapper';
import { PaxType } from '@booking/entities/pax-type';
import { BookingPaxTypeDocumentProps, PaxBookingDocuments } from '@app/entities/booking-documents';
import { TravelerPaxResponse } from '@pax/entities/traveler-pax';
import { getPaxGender } from '@pax/mappers/pax-gender.mapper';
import { isNullOrUndefined } from '@app/util/util';
import { getUTCDate } from '@flight-results/entities/date-validation';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { dobToDate } from '@ux/mappers/date.mapper';

export const GUEST_PAYLOAD_KEY = 'GUEST';

const ROOM_PAYLOAD_KEY = 'ROOM';
const MOCK_DATA_KEY = 'IsMockData';

export function hotelBookingPayloadMapper(
  form: UntypedFormGroup,
  ValidationId: string,
  arrangement: BookingArrangementVM,
  billingInfo: BillingResponse,
  rooms: Room[],
  guests: TravelerItemUI[],
  documents: PaxBookingDocuments,
  assistanceInfo: { optionId: number, searchId: string },
  bookingSource: HotelProvider,
  isByhours: boolean
): HotelBookingPayload {
  const travelers = getTravelers(arrangement, rooms, guests, documents, bookingSource, isByhours);
  const createIsEnabledByBilling = createAt(billingInfo);
  const createIsEnabledByAssistance = !isNullOrUndefined(assistanceInfo?.optionId);
  return {
    arrangement: {
      AdditionalInformation: form.value.Comments,
      ValidateOptionId: ValidationId,
      TravelItineraryId: 0,
      Travelers: travelers,
    },
    billing: {
      createIsEnabled: createIsEnabledByBilling,
      payload: createIsEnabledByBilling ? payloadToCreate(billingInfo, arrangement) : {} as BillingPayload
    },
    assistance: {
      createIsEnabled: createIsEnabledByAssistance,
      payload: createIsEnabledByAssistance ?
        assistanceBookPayload(assistanceInfo?.searchId, travelers, assistanceInfo?.optionId) : {} as AssistanceBookPayload
    },
  };
}

function formatTraveler(traveler: Travelers, bookingSource: HotelProvider): Travelers {
  const travelerName = `${traveler[TRAVELER_FORM_INPUTS.FIRST_NAME]} ${traveler[TRAVELER_FORM_INPUTS.SECOND_NAME]}`.trim();
  const { BonvoyNumber, ...editedTraveler } = traveler;
  return omit({
    ...editedTraveler,
    LoyaltyAccountId: bookingSource === HotelProvider.MarriottBonvoy ? traveler.BonvoyNumber : null,
    [TRAVELER_FORM_INPUTS.FIRST_NAME]: travelerName,
    [TRAVELER_FORM_INPUTS.PAX_TYPE]: getPaxType(traveler),
  },
  [
    TRAVELER_FORM_INPUTS.SECOND_NAME,
    TRAVELER_FORM_INPUTS.USER_ID,
    TRAVELER_FORM_INPUTS.INTERNATIONAL_DOCUMENT_REQUIRED
  ]
  );
}

function getTravelers(
  arrangement: BookingArrangementVM,
  rooms: Room[],
  guests: TravelerItemUI[],
  documents: PaxBookingDocuments,
  bookingSource: HotelProvider,
  isByHours: boolean
): Travelers[] {
  const allTravelers = getAllTravelers(guests, documents).map(traveler => ({
    ...formatTraveler(traveler, bookingSource),
    Phone: arrangement.Phone,
    Email: arrangement.Email,
  }));

  let travelers = allTravelers as Travelers[];

  rooms.forEach((room, roomIndex) => {
    const roomNumber = roomIndex + 1;
    travelers = getTravelersWithRoomNumber(travelers, room, roomNumber);
  });

  let RTVLS = [];

  rooms.forEach((room, roomIndex) => {
    const thisRoom = roomIndex + 1;
    const childrenAges = rooms[roomIndex].childrenAges;
    const roomTravelers = travelers.filter(traveler =>
      !isNullOrUndefined(traveler.RoomNumber) && traveler.RoomNumber === thisRoom);
    RTVLS = RTVLS.concat(getTravelersWithAges(roomTravelers, childrenAges));
  });

  RTVLS = RTVLS.filter(traveler => !isNullOrUndefined(traveler.RoomNumber));
  if (isByHours) {
    return RTVLS.length > 0 ? [RTVLS[0]] : [];
  }
  return RTVLS;
}

function getTravelersWithRoomNumber(travelers: Travelers[], room: Room, roomNumber: number): Travelers[] {
  let adults = room[TravelerTypes.ADULTS];
  let children = room[TravelerTypes.CHILDREN];
  return travelers.map((traveler: Travelers) => {
    if (isNullOrUndefined(traveler.RoomNumber) && (adults > 0 || children > 0)) {
      if (paxByGuestRoom[traveler.PaxType] === TravelerTypes.ADULTS) adults--;
      else if (paxByGuestRoom[traveler.PaxType] === TravelerTypes.CHILDREN) children--;
      const defaultInfo = { ...traveler, RoomNumber: roomNumber };
      if (defaultInfo.IsMockData) {
        return omit({
          ...defaultInfo,
          FirstName: `${traveler.FirstName} ${ROOM_PAYLOAD_KEY} ${roomNumber}`,
          LastName: `${traveler.LastName} ${ROOM_PAYLOAD_KEY} ${roomNumber}`,
          DocumentNumber: `${traveler.DocumentNumber}R${roomNumber}`,
        }, [
          MOCK_DATA_KEY
        ]);
      } else return defaultInfo;
    } else return traveler;
  });
}

function getTravelersWithAges(travelers: Travelers[], childrenAges: number[]): Travelers[] {
  const childrens = travelers
    .filter(traveler => paxByGuestRoom[traveler.PaxType] === TravelerTypes.CHILDREN)
    .map((traveler, index) => {
      const date = dobToDate(traveler.DOB);
      const age = childrenAges[index];
      date.setFullYear(date.getFullYear() - age);
      date.setDate(date.getDate() - 1);
      return {
        ...traveler,
        DOB: formatTravelerDate(date)
      };
    });

  const adults = travelers
    .filter(traveler => paxByGuestRoom[traveler.PaxType] === TravelerTypes.ADULTS)
    .map(traveler => ({
      ...traveler,
      DOB: formatTravelerDate(dobToDate(traveler.DOB))
    }));

  return [].concat(adults).concat(childrens);
}

function getAllTravelers(
  guests: TravelerItemUI[], documents: PaxBookingDocuments
): Travelers[] {
  return guests.map((guest, index) => {
    if (!isEmpty(guest.defaultData)) {
      const guestDataUI: TravelerPaxResponse = guest.defaultData;
      const DOB = getUTCDate(guestDataUI.dob);
      DOB.setHours(0, 0, 0, 0);
      return {
        PaxType: guestDataUI.pax_type,
        FirstName: guestDataUI.first_name,
        LastName: guestDataUI.last_name,
        SecondName: guestDataUI.middle_name,
        DocumentNumber: guestDataUI.document_number || null,
        ConfigurationDocumentId: guestDataUI.configuration_document_id || null,
        Nationality: guestDataUI.nationality,
        DOB: formatTravelerDate(DOB),
        FrequentFlyer: '',
        FrequentFlyerAirline: '',
        Gender: getPaxGender(guestDataUI.gender),
        BonvoyNumber: guestDataUI.bonvoy_number
      };
    } else {
      const documentDefault = documents[guest.TravelerType][0] as BookingPaxTypeDocumentProps;
      const paxType = PaxType[guest.TravelerType];
      const DOB = new Date();
      if (paxType === PaxType.ADULTS) {
        DOB.setFullYear(DOB.getFullYear() - 20);
      }
      return {
        PaxType: PaxType[guest.TravelerType],
        FirstName: `${GUEST_PAYLOAD_KEY} FIRSTNAME ${guest.OrdinalKey}`,
        LastName: `${GUEST_PAYLOAD_KEY} LASTNAME ${guest.OrdinalKey}`,
        SecondName: '',
        DocumentNumber: `${GUEST_PAYLOAD_KEY}DOC${guest.OrdinalKey}`,
        ConfigurationDocumentId: documentDefault?.DOCUMENT_ID,
        Nationality: ENV.countryInfo.iso_code,
        DOB: formatTravelerDate(DOB),
        FrequentFlyer: '',
        FrequentFlyerAirline: '',
        Gender: UserGender.Male,
        IsMockData: true,
      };
    }
  }).filter(item => !isEmpty(item));
}

function getPaxType(traveler: Travelers): PaxType {
  return traveler.PaxType === PaxType.INFANTS ? PaxType.CHILDREN : traveler.PaxType;
}
